import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Business, BusinessReview, businessCategories } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Building2, MessageSquare, Check, X, Trash2, Eye, Phone, Mail, Star, Upload, Download, FileSpreadsheet, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { ko } from "date-fns/locale";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toArray } from "@/lib/array";
import { normalizeImageUrl } from "@/lib/image-url";

const regions = [
  { value: "ALL", label: "전체" },
  { value: "NSW", label: "뉴사우스웨일스" },
  { value: "VIC", label: "빅토리아" },
  { value: "QLD", label: "퀸즐랜드" },
  { value: "SA", label: "남호주" },
  { value: "WA", label: "서호주" },
  { value: "TAS", label: "태즈매니아" },
  { value: "NT", label: "노던 테리토리" },
  { value: "ACT", label: "수도 준주" },
];

export function AdminBusinessDirectory() {
  const { toast } = useToast();
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Fetch businesses
  const { data: rawBusinesses, isLoading: businessesLoading } = useQuery({
    queryKey: ["/api/business-directory"],
  });

  // Safe array conversion - prevents ".filter() is not a function" errors
  const businesses = toArray<Business>(rawBusinesses);

  // Fetch all reviews (for review management tab)
  const { data: rawReviews, isLoading: reviewsLoading } = useQuery({
    queryKey: ["/api/business-directory/reviews/all"],
    queryFn: async () => {
      // Since we don't have a dedicated endpoint for all reviews, we'll fetch them per business
      const reviewsPromises = businesses.map(async (business) => {
        try {
          const response = await fetch(`/api/business-directory/${business.id}/reviews`);
          if (!response.ok) return [];
          const reviews = await response.json() as BusinessReview[];
          return (reviews as any[])?.map((review: BusinessReview) => ({
            ...review,
            businessName: business.businessNameKo || business.businessName,
          }));
        } catch {
          return [];
        }
      });
      const reviewsArrays = await Promise.all(reviewsPromises);
      return reviewsArrays.flat();
    },
    enabled: businesses.length > 0,
  });

  // Safe array conversion for reviews
  const allReviews = toArray<BusinessReview & { businessName?: string }>(rawReviews);

  // Delete business mutation
  const deleteBusinessMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/business-directory/${id}`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/business-directory"] });
      toast({
        title: "삭제 완료",
        description: "업체가 삭제되었습니다.",
      });
    },
  });

  // Approve business mutation
  const approveBusinessMutation = useMutation({
    mutationFn: (id: string) => apiRequest("PATCH", `/api/business-directory/${id}`, { status: "active" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/business-directory"] });
      toast({
        title: "승인 완료",
        description: "업체가 승인되었습니다.",
      });
    },
  });

  // Reject business mutation
  const rejectBusinessMutation = useMutation({
    mutationFn: (id: string) => apiRequest("PATCH", `/api/business-directory/${id}`, { status: "rejected" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/business-directory"] });
      toast({
        title: "거부 완료",
        description: "업체가 거부되었습니다.",
      });
    },
  });

  // Delete review mutation
  const deleteReviewMutation = useMutation({
    mutationFn: ({ businessId, reviewId }: { businessId: string; reviewId: string }) =>
      apiRequest("DELETE", `/api/business-directory/${businessId}/reviews/${reviewId}`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/business-directory/reviews/all"] });
      queryClient.invalidateQueries({ queryKey: ["/api/business-directory"] });
      toast({
        title: "삭제 완료",
        description: "리뷰가 삭제되었습니다.",
      });
    },
  });

  const handleViewDetail = (business: Business) => {
    setSelectedBusiness(business);
    setDetailDialogOpen(true);
  };

  const getCategoryLabel = (categoryId: string) => {
    return businessCategories.find(c => c.id === categoryId)?.label || categoryId;
  };

  const getRegionLabel = (regionId: string | null) => {
    if (!regionId) return "전체";
    return regions.find(r => r.value === regionId)?.label || regionId;
  };

  const getStatusBadge = (status: string) => {
    const colorMap: Record<string, string> = {
      active: "bg-green-500/10 text-green-700 dark:text-green-400",
      pending: "bg-yellow-500/10 text-yellow-700 dark:text-yellow-400",
      rejected: "bg-red-500/10 text-red-700 dark:text-red-400",
      suspended: "bg-gray-500/10 text-gray-700 dark:text-gray-400",
    };
    const labelMap: Record<string, string> = {
      active: "승인",
      pending: "대기중",
      rejected: "거부됨",
      suspended: "정지됨",
    };
    return (
      <Badge className={colorMap[status] || colorMap.pending}>
        {labelMap[status] || status}
      </Badge>
    );
  };

  const getMembershipBadge = (tier: string | null) => {
    if (tier === "platinum") {
      return <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">플래티넘</Badge>;
    }
    if (tier === "premium") {
      return <Badge className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white">프리미엄</Badge>;
    }
    return <Badge variant="outline">무료</Badge>;
  };

  // Calculate statistics
  const stats = {
    total: businesses.length,
    active: businesses.filter(b => b.status === "active").length,
    pending: businesses.filter(b => b.status === "pending").length,
    rejected: businesses.filter(b => b.status === "rejected").length,
    totalReviews: allReviews.length,
    totalViews: businesses.reduce((sum, b) => sum + (b.views || 0), 0),
  };

  return (
    <div className="space-y-4">
      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">전체 업체</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">승인됨</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.active}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">대기중</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">거부됨</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats.rejected}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">총 리뷰</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalReviews}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">총 조회수</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalViews}</div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="businesses" className="space-y-4">
        <TabsList>
          <TabsTrigger value="businesses" data-testid="tab-businesses">
            <Building2 className="h-4 w-4 mr-2" />
            업체 관리
          </TabsTrigger>
          <TabsTrigger value="csv-upload" data-testid="tab-csv-upload">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            CSV 업로드
          </TabsTrigger>
          <TabsTrigger value="reviews" data-testid="tab-reviews">
            <MessageSquare className="h-4 w-4 mr-2" />
            리뷰 관리
          </TabsTrigger>
        </TabsList>

        {/* Businesses Management */}
        <TabsContent value="businesses" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>업체 목록</CardTitle>
            </CardHeader>
            <CardContent>
              {businessesLoading ? (
                <div className="text-center py-8">로딩 중...</div>
              ) : businesses.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  등록된 업체가 없습니다
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>업체명</TableHead>
                        <TableHead>업종</TableHead>
                        <TableHead>지역</TableHead>
                        <TableHead>멤버십</TableHead>
                        <TableHead>상태</TableHead>
                        <TableHead>연락처</TableHead>
                        <TableHead>통계</TableHead>
                        <TableHead>등록일</TableHead>
                        <TableHead className="text-right">작업</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {businesses.map((business) => (
                        <TableRow key={business.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {business.logoUrl && (
                                <img
                                  src={normalizeImageUrl(business.logoUrl) || ''}
                                  alt=""
                                  className="w-8 h-8 rounded object-cover"
                                />
                              )}
                              <div>
                                <div className="font-medium">
                                  {business.businessNameKo || business.businessName}
                                </div>
                                {business.businessNameKo && business.businessName !== business.businessNameKo && (
                                  <div className="text-xs text-muted-foreground">
                                    {business.businessName}
                                  </div>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{getCategoryLabel(business.category)}</TableCell>
                          <TableCell>{getRegionLabel(business.region)}</TableCell>
                          <TableCell>{getMembershipBadge(business.membershipTier)}</TableCell>
                          <TableCell>{getStatusBadge(business.status)}</TableCell>
                          <TableCell>
                            <div className="text-sm space-y-1">
                              {business.contactPhone && (
                                <div className="flex items-center gap-1">
                                  <Phone className="h-3 w-3" />
                                  {business.contactPhone}
                                </div>
                              )}
                              {business.contactEmail && (
                                <div className="flex items-center gap-1">
                                  <Mail className="h-3 w-3" />
                                  {business.contactEmail}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm space-y-1">
                              <div className="flex items-center gap-1">
                                <Eye className="h-3 w-3" />
                                {business.views || 0}
                              </div>
                              <div className="flex items-center gap-1">
                                <Star className="h-3 w-3" />
                                {Number(business.averageRating).toFixed(1)} ({business.reviewCount})
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {format(new Date(business.createdAt), "yyyy-MM-dd", { locale: ko })}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewDetail(business)}
                                data-testid={`button-view-${business.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {business.status === "pending" && (
                                <>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => approveBusinessMutation.mutate(business.id)}
                                    className="text-green-600 hover:text-green-700"
                                    data-testid={`button-approve-${business.id}`}
                                  >
                                    <Check className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => rejectBusinessMutation.mutate(business.id)}
                                    className="text-red-600 hover:text-red-700"
                                    data-testid={`button-reject-${business.id}`}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  if (confirm("정말 삭제하시겠습니까?")) {
                                    deleteBusinessMutation.mutate(business.id);
                                  }
                                }}
                                className="text-red-600 hover:text-red-700"
                                data-testid={`button-delete-${business.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reviews Management */}
        <TabsContent value="reviews" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>리뷰 목록</CardTitle>
            </CardHeader>
            <CardContent>
              {reviewsLoading ? (
                <div className="text-center py-8">로딩 중...</div>
              ) : allReviews.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  등록된 리뷰가 없습니다
                </div>
              ) : (
                <div className="space-y-4">
                  {allReviews.map((review) => (
                    <Card key={review.id}>
                      <CardContent className="pt-6">
                        <div className="flex items-start gap-4">
                          <Avatar>
                            <AvatarImage src={review.userPhotoUrl || undefined} />
                            <AvatarFallback>
                              {review.userDisplayName?.[0]?.toUpperCase() || "U"}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <div className="font-semibold">{review.userDisplayName}</div>
                                <div className="text-sm text-muted-foreground">
                                  {review.businessName}
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                  <div className="flex">
                                    {[...Array(5)].map((_, i) => (
                                      <Star
                                        key={i}
                                        className={`h-4 w-4 ${
                                          i < review.rating
                                            ? "fill-yellow-400 text-yellow-400"
                                            : "text-muted"
                                        }`}
                                      />
                                    ))}
                                  </div>
                                  <span className="text-sm text-muted-foreground">
                                    {format(new Date(review.createdAt), "yyyy-MM-dd HH:mm", {
                                      locale: ko,
                                    })}
                                  </span>
                                </div>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  if (confirm("정말 삭제하시겠습니까?")) {
                                    deleteReviewMutation.mutate({
                                      businessId: review.businessId,
                                      reviewId: review.id,
                                    });
                                  }
                                }}
                                className="text-red-600 hover:text-red-700"
                                data-testid={`button-delete-review-${review.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                            {review.title && (
                              <h4 className="font-semibold mb-1">{review.title}</h4>
                            )}
                            <p className="text-sm whitespace-pre-wrap">{review.content}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CSV Upload Tab */}
        <TabsContent value="csv-upload" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>CSV 파일로 업체 대량 등록</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Instructions */}
              <div className="bg-muted/50 p-4 rounded-lg space-y-3">
                <h3 className="font-semibold text-sm">📋 사용 방법</h3>
                <ol className="text-sm space-y-2 list-decimal list-inside text-muted-foreground">
                  <li>CSV 템플릿을 다운로드하거나 형식에 맞춰 파일을 준비하세요</li>
                  <li>업체명(한글), 업체명(영문), 전화번호, 지역, 카테고리, 주소를 입력하세요</li>
                  <li>준비된 CSV 파일을 업로드하세요</li>
                  <li>업로드된 업체는 "pending" 상태로 등록되며 승인이 필요합니다</li>
                </ol>
              </div>

              {/* Download Template */}
              <div className="space-y-2">
                <Label>CSV 템플릿 다운로드</Label>
                <Button
                  variant="outline"
                  onClick={() => {
                    const csvContent = `businessNameKo,businessName,contactPhone,region,category,address
한식당,Korean Restaurant,02-1234-5678,NSW,restaurant,123 Main St Sydney NSW
일식당,Japanese Restaurant,03-9876-5432,VIC,restaurant,456 Queen St Melbourne VIC
카페,Cafe,07-1111-2222,QLD,cafe,789 King St Brisbane QLD`;
                    const blob = new Blob([csvContent], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'business-directory-template.csv';
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  data-testid="button-download-template"
                >
                  <Download className="h-4 w-4 mr-2" />
                  템플릿 다운로드 (.csv)
                </Button>
                <p className="text-xs text-muted-foreground">
                  CSV 형식: businessNameKo, businessName, contactPhone, region, category, address
                </p>
              </div>

              {/* Upload CSV */}
              <div className="space-y-2">
                <Label>CSV 파일 업로드</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept=".csv"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setCsvFile(file);
                      }
                    }}
                    data-testid="input-csv-file"
                  />
                  <Button
                    onClick={async () => {
                      if (!csvFile) {
                        toast({
                          title: "오류",
                          description: "CSV 파일을 선택해주세요.",
                          variant: "destructive",
                        });
                        return;
                      }

                      setIsUploading(true);
                      try {
                        const formData = new FormData();
                        formData.append('file', csvFile);

                        const response = await fetch('/api/business-directory/bulk-upload', {
                          method: 'POST',
                          body: formData,
                          credentials: 'include',
                        });

                        if (!response.ok) {
                          const error = await response.json() as { error?: string };
                          throw new Error(error.error || 'Upload failed');
                        }

                        const result = await response.json() as { created?: number; failed?: number };
                        
                        queryClient.invalidateQueries({ queryKey: ["/api/business-directory"] });
                        
                        toast({
                          title: "업로드 완료",
                          description: `${result.created}개 업체가 등록되었습니다. (실패: ${result.failed || 0}개)`,
                        });

                        setCsvFile(null);
                        // Reset file input
                        const fileInput = document.querySelector('[data-testid="input-csv-file"]') as HTMLInputElement;
                        if (fileInput) fileInput.value = '';

                      } catch (error: any) {
                        console.error('CSV upload error:', error);
                        toast({
                          title: "업로드 실패",
                          description: error.message || "CSV 파일 업로드 중 오류가 발생했습니다.",
                          variant: "destructive",
                        });
                      } finally {
                        setIsUploading(false);
                      }
                    }}
                    disabled={!csvFile || isUploading}
                    data-testid="button-upload-csv"
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        업로드 중...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        업로드
                      </>
                    )}
                  </Button>
                </div>
                {csvFile && (
                  <p className="text-sm text-muted-foreground">
                    선택된 파일: {csvFile.name} ({(csvFile.size / 1024).toFixed(2)} KB)
                  </p>
                )}
              </div>

              {/* Format Requirements */}
              <div className="space-y-2">
                <Label>필수 필드</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                  <div className="bg-muted/50 p-2 rounded">
                    <span className="font-medium">businessNameKo</span>
                    <p className="text-xs text-muted-foreground">업체명 (한글)</p>
                  </div>
                  <div className="bg-muted/50 p-2 rounded">
                    <span className="font-medium">businessName</span>
                    <p className="text-xs text-muted-foreground">업체명 (영문)</p>
                  </div>
                  <div className="bg-muted/50 p-2 rounded">
                    <span className="font-medium">contactPhone</span>
                    <p className="text-xs text-muted-foreground">전화번호</p>
                  </div>
                  <div className="bg-muted/50 p-2 rounded">
                    <span className="font-medium">region</span>
                    <p className="text-xs text-muted-foreground">지역 (NSW, VIC 등)</p>
                  </div>
                  <div className="bg-muted/50 p-2 rounded">
                    <span className="font-medium">category</span>
                    <p className="text-xs text-muted-foreground">카테고리</p>
                  </div>
                  <div className="bg-muted/50 p-2 rounded">
                    <span className="font-medium">address</span>
                    <p className="text-xs text-muted-foreground">주소</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Business Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>업체 상세 정보</DialogTitle>
            <DialogDescription>
              {selectedBusiness?.businessNameKo || selectedBusiness?.businessName}
            </DialogDescription>
          </DialogHeader>
          {selectedBusiness && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-semibold mb-2">기본 정보</h4>
                  <dl className="space-y-2 text-sm">
                    <div>
                      <dt className="text-muted-foreground">업체명 (영문)</dt>
                      <dd className="font-medium">{selectedBusiness.businessName}</dd>
                    </div>
                    {selectedBusiness.businessNameKo && (
                      <div>
                        <dt className="text-muted-foreground">업체명 (한글)</dt>
                        <dd className="font-medium">{selectedBusiness.businessNameKo}</dd>
                      </div>
                    )}
                    <div>
                      <dt className="text-muted-foreground">업종</dt>
                      <dd className="font-medium">{getCategoryLabel(selectedBusiness.category)}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">지역</dt>
                      <dd className="font-medium">{getRegionLabel(selectedBusiness.region)}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">상태</dt>
                      <dd className="font-medium">{getStatusBadge(selectedBusiness.status)}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">멤버십</dt>
                      <dd className="font-medium">{getMembershipBadge(selectedBusiness.membershipTier)}</dd>
                    </div>
                  </dl>
                </div>
                <div>
                  <h4 className="text-sm font-semibold mb-2">연락처 정보</h4>
                  <dl className="space-y-2 text-sm">
                    {selectedBusiness.contactPhone && (
                      <div>
                        <dt className="text-muted-foreground">전화</dt>
                        <dd className="font-medium">{selectedBusiness.contactPhone}</dd>
                      </div>
                    )}
                    {selectedBusiness.contactEmail && (
                      <div>
                        <dt className="text-muted-foreground">이메일</dt>
                        <dd className="font-medium">{selectedBusiness.contactEmail}</dd>
                      </div>
                    )}
                    {selectedBusiness.website && (
                      <div>
                        <dt className="text-muted-foreground">웹사이트</dt>
                        <dd className="font-medium">
                          <a
                            href={selectedBusiness.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            {selectedBusiness.website}
                          </a>
                        </dd>
                      </div>
                    )}
                    {selectedBusiness.address && (
                      <div>
                        <dt className="text-muted-foreground">주소</dt>
                        <dd className="font-medium">
                          {selectedBusiness.address}
                          {selectedBusiness.city && `, ${selectedBusiness.city}`}
                          {selectedBusiness.postcode && ` ${selectedBusiness.postcode}`}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              </div>

              {selectedBusiness.description && (
                <div>
                  <h4 className="text-sm font-semibold mb-2">상세 설명</h4>
                  <p className="text-sm whitespace-pre-wrap">{selectedBusiness.description}</p>
                </div>
              )}

              {selectedBusiness.businessHours && (
                <div>
                  <h4 className="text-sm font-semibold mb-2">영업 시간</h4>
                  <p className="text-sm whitespace-pre-wrap">{selectedBusiness.businessHours}</p>
                </div>
              )}

              <div>
                <h4 className="text-sm font-semibold mb-2">통계</h4>
                <dl className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <dt className="text-muted-foreground">조회수</dt>
                    <dd className="text-2xl font-bold">{selectedBusiness.views || 0}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">클릭수</dt>
                    <dd className="text-2xl font-bold">{selectedBusiness.clicks || 0}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">북마크</dt>
                    <dd className="text-2xl font-bold">{selectedBusiness.bookmarks || 0}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">평균 평점</dt>
                    <dd className="text-2xl font-bold">
                      {Number(selectedBusiness.averageRating).toFixed(1)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">리뷰 수</dt>
                    <dd className="text-2xl font-bold">{selectedBusiness.reviewCount}</dd>
                  </div>
                </dl>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
